import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Image, Alert } from 'react-bootstrap';
import { getStaticStaffNameAndNumber } from '../helpers/dataHelper';
import { useTranslation } from 'react-i18next';
import SEOComponent from '../components/SEOComponent';

const Donate = () => {
    const accountName = process.env.REACT_APP_SHANDE_ACCOUNT_NAME;
    const cimbAccount = process.env.REACT_APP_SHANDE_CIMB_BANK_ACCOUNT;
    const cimbAccount2 = process.env.REACT_APP_SHANDE_CIMB_BANK_ACCOUNT_2;
    const tngUrl = process.env.REACT_APP_TOUCH_N_GO_URL;
    const marketingData = getStaticStaffNameAndNumber("Marketing");
    const {t} = useTranslation();

    const redirectToTng = () => {
        window.location.href = tngUrl;
    }
    
    // State to manage the text of the copy buttons
    const [copyButtonText, setCopyButtonText] = useState({
        cimb: 'Copy',
        publicBank: 'Copy'
    });

    // Function to copy text to clipboard and provide user feedback
    const handleCopyToClipboard = (text, buttonKey) => {
        navigator.clipboard.writeText(text).then(() => {
            // Update the specific button's text to "Copied!"
            setCopyButtonText(prev => ({ ...prev, [buttonKey]: 'Copied!' }));
            
            // Revert the button text back to "Copy" after 2 seconds
            setTimeout(() => {
                setCopyButtonText(prev => ({ ...prev, [buttonKey]: 'Copy' }));
            }, 2000);
        }).catch(err => {
            console.error('Failed to copy text: ', err);
            // You could show an error alert here
        });
    };
    
    // Custom styles
    const styles = {
        gradientBg: {
            background: 'var(--landing-bg)',
            minHeight: '100vh',
            padding: '2rem 0',
            fontFamily: "'Noto Sans SC', 'Lato', sans-serif"
        },
        card: {
            border: 'none',
            borderRadius: 'var(--landing-radius)',
            boxShadow: 'var(--landing-shadow)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            height: '100%'
        },
        cardHover: {
            transform: 'translateY(-5px)',
            boxShadow: '0 12px 34px rgba(248, 187, 208, 0.35)'
        },
        tngButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
        }
    };

    const donateStructuredData = {
        "@context": "https://schema.org",
        "@type": "DonateAction",
        "name": "Donate to Shan De Children's Welfare Home",
        "description": "Make a donation to support disadvantaged children at Shan De Children's Welfare Home. We accept bank transfers and Touch 'n Go payments.",
        "url": "https://www.shandejohor.org.my/donate",
        "recipient": {
            "@type": "NGO",
            "name": "Pertubuhan Kebajikan Shan De",
            "address": {
                "@type": "PostalAddress",
                "addressLocality": "Johor Bahru",
                "addressRegion": "Johor",
                "addressCountry": "MY"
            }
        }
    };

    return (
        <>
        <SEOComponent 
            title="Donate Now"
            description="Make a donation to Shan De Children's Welfare Home and help disadvantaged children in Johor Bahru. We accept bank transfers and Touch 'n Go payments. Your donation makes a difference."
            keywords="donate, donation, bank transfer, tng, Touch n Go, duitnow, charity donation, help children, financial support"
            url="/donate"
            structuredData={donateStructuredData}
        />
        <div className="donate-page" style={styles.gradientBg}>
            <Container className="py-4 py-md-5">
                <div className="text-center mb-5">
                    <h1 className="landing-heading fw-bold">{t('makeDonation')}</h1>
                    <p className="text-muted fs-5 mt-2">
                        {t('makeDonationDescription')}
                    </p>
                </div>

                <Row className="g-4 justify-content-center">
                    {/* Bank Transfer Card */}
                    <Col md={6} lg={5}>
                        <Card style={styles.card}>
                            <Card.Body className="p-4 flex-column cimb-bg">
                                <div className="d-flex align-items-center mb-3">
                                    <i className="fas fa-university text-primary fs-2 me-3"></i>
                                    <Card.Title as="h3" className="mb-0 fw-bold">{t('bankTransfer')}</Card.Title>
                                </div>
                                <Card.Text className="text-muted mb-4">
                                    {t('bankTransferDescription')}
                                </Card.Text>
                                <div className="mt-auto">
                                    <p className="fw-semibold mb-1">Account Name:</p>
                                    <p className="mb-3">{accountName}</p>
                                    
                                    <p className="fw-semibold mb-1">CIMB Bank:</p>
                                    <div className="input-group mb-3">
                                        <span className="input-group-text bg-light border-end-0">{cimbAccount}</span>
                                        <Button
                                            variant={copyButtonText.cimb === 'Copy' ? 'primary' : 'success'}
                                            className={copyButtonText.cimb === 'Copy' ? 'donate-copy-button' : 'donate-copy-button-success'}
                                            onClick={() => handleCopyToClipboard(cimbAccount, 'cimb')}
                                        >
                                            {copyButtonText.cimb}
                                        </Button>
                                    </div>

                                    <p className="fw-semibold mb-1">{t('publicBankTransfer')}</p>
                                    <div className="input-group">
                                        <span className="input-group-text bg-light border-end-0">{cimbAccount2}</span>
                                        <Button
                                            variant={copyButtonText.publicBank === 'Copy' ? 'primary' : 'success'}
                                            className={copyButtonText.publicBank === 'Copy' ? 'donate-copy-button' : 'donate-copy-button-success'}
                                            onClick={() => handleCopyToClipboard(cimbAccount2, 'publicBank')}
                                        >
                                            {copyButtonText.publicBank}
                                        </Button>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* E-Wallets Card */}
                    <Col md={6} lg={5}>
                        <Card style={styles.card}>
                            <Card.Body className="p-4 d-flex flex-column text-center">
                                <div className="d-flex align-items-center justify-content-left mb-3">
                                    <i className="fas fa-qrcode text-success fs-2 me-3"></i>
                                    <Card.Title as="h3" className="mb-0 fw-bold">{t('eWallets')}</Card.Title>
                                </div>
                                <Card.Text className="text-muted mb-4 text-start">
                                    {t('eWalletsDescription')}
                                </Card.Text>
                                <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                                    <p className="fw-semibold">DuitNow QR</p>
                                    <Image className="mw-100 mh-600 mb-2 shadow-sm" src="assets/img/donation_details/duitnow_QR_code_only.JPG" alt="Duit Now" rounded />
                                    <p className="text-muted small mb-4">{t('scanWithYourBankingApp')}</p>
                                    <Button variant="info" className="w-100 py-2 border-0 donate-tng-button" style={styles.tngButton} onClick={redirectToTng}>
                                         <Image src="assets/img/tng_icon.png" roundedCircle alt="TNG eWallet logo" width={32} height={32} />
                                        {t('donateWithTNG')}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Cheque & Contact Info */}
                    <Col lg={10}>
                         <Card style={styles.card}>
                            <Card.Body className="p-4">
                                <Row className="g-4">
                                    <Col md={6}>
                                        <div className="d-flex align-items-center mb-3">
                                            <i className="fas fa-money-check-alt fs-2 me-3" style={{color: 'var(--landing-accent)'}}></i>
                                            <h4 className="fw-bold mb-0">{t('chequeDonations')}</h4>
                                        </div>
                                        <p className="text-muted">{t('chequeDonationsDescription')}</p>
                                        <p className="fw-bold fs-5">{accountName}</p>
                                    </Col>
                                    <Col md={6} className="border-md-start">
                                         <Alert variant="secondary" className="h-100 d-flex">
                                            <i className="fas fa-receipt fs-2 me-3"></i>
                                            <div>
                                                <p className="fw-bold mb-1">{t('needAnOfficialReceipt')}</p>
                                                <p className="mb-0 small">{t('needAnOfficialReceiptDescription')}</p>
                                                {
                                                    marketingData && marketingData.map((item, index) => (
                                                        <div key={index} className="d-flex align-items-center mt-1 mb-0">
                                                            <span className="fw-semibold text-truncate contact-label">{item.name}:</span>
                                                            <span className="d-inline-flex align-items-center">
                                                                <a className="text-decoration-none" href={`https://api.whatsapp.com/send?phone=6${item.contact}`} target="_blank" rel="noreferrer">+6{item.contact}</a>
                                                                <a className="ms-2 d-inline-flex align-items-center" href={`https://api.whatsapp.com/send?phone=6${item.contact}`} target="_blank" rel="noreferrer" aria-label={`WhatsApp ${item.name}`}>
                                                                    <Image src="assets/img/WhatsApp.svg.webp" alt="WhatsApp" width={20} height={20} />
                                                                </a>
                                                            </span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </Alert>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
        </>
    );
};

export default Donate;
